import {Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {ToastHandlerService} from '../../../shared/services/toast-handler.service';
import {BackendPaginationResponse} from '../../../shared/models/backend-pagination-response';
import {environment} from '../../../../environments/environment';
import {catchError, map} from 'rxjs/operators';
import {Observable, throwError} from 'rxjs';
import {CustomerEntity} from '../../../admin/modules/admin-customer/models/customer-entity';
import {
  CommonMissionDocumentUpload
} from '../components/common-mission-document-upload/common-mission-document-upload.component';
import {Mission} from '../models/mission';
import {TranslateService} from '@ngx-translate/core';
import {CommonMissionAnomaly} from '../components/common-mission-anomaly/common-mission-anomaly.component';
import {NbDialogRef, NbDialogService} from '@nebular/theme';
import {
  CommonMissionDatetimeDialogComponent
} from '../components/common-mission-form/common-mission-datetime-dialog/common-mission-datetime-dialog.component';
import {AnomalyTypesGroup} from '../models/anomalyTypesGroup';
import {CommonMissionContactDialogComponent} from '../components/common-mission-form/common-mission-contact-dialog/common-mission-contact-dialog.component';
import {CommonMissionReparationsDialogComponent} from '../components/common-mission-type-selector/components/common-mission-reparations-dialog/common-mission-reparations-dialog.component';
import {CommonMissionDelayedDialogComponent} from '../components/common-mission-form/common-mission-delayed-dialog/common-mission-delayed-dialog.component';
import * as moment from 'moment/moment';
import {
  CommonMissionAddressDialogComponent
} from '../components/common-mission-form/common-mission-address-dialog/common-mission-address-dialog.component';
import { USER_ROLE } from '../../../shared/models/user';
import { ExportCsvService } from '../../../shared/services/export-csv.service';
import { BackendFilteringObject } from '../../../shared/models/grid-config.interface';
import {
  CustomerAnomalySubtype,
  PopValetAnomalySubtype,
  ValetAnomalySubtype
} from '../../common-anomalies/models/anomaly';

@Injectable({
  providedIn: 'root'
})
export class CommonMissionService {

  private availableDocumentTypes = [];

  private anomalyTypesGroups: AnomalyTypesGroup[];
  private datetimeDialog: NbDialogRef<CommonMissionDatetimeDialogComponent>;
  private addressDialog: NbDialogRef<CommonMissionAddressDialogComponent>;
  private contactDialog: NbDialogRef<CommonMissionContactDialogComponent>;
  private reparationsDialog: NbDialogRef<CommonMissionReparationsDialogComponent>;
  private delayedDialog: NbDialogRef<CommonMissionDelayedDialogComponent>;

  constructor(private http: HttpClient,

              private translateService: TranslateService,
              private toastService: ToastHandlerService,
              private exportService: ExportCsvService,
              private dialogService: NbDialogService) {
    this.anomalyTypesGroups = this.getMissionAnomaliesTypesGroups();
  }

  public getMissionDetail(missionId: string): Promise<Mission> {
    return this.http.get(`${environment.backendUrl}admin/missions/mission/${missionId}`)
      .pipe(
        map((res: any) => res.data),
        catchError(err => {
          this.toastService.showErrorToastsResponse(err);
          this.toastService.showErrorToasts(
            this.translateService.instant('modules.dashboard.service.get_mission_error')
          );
          return throwError(err);
        })
      ).toPromise();
  }


  public updateMissionInfo(missionId: string, data: any): Promise<Mission> {
    return this.http.put(`${environment.backendUrl}admin/missions/info/${missionId}`,
      data)
      .pipe(
        map((res: any) => {
          this.toastService.showSuccessToast(
            this.translateService.instant('modules.dashboard.service.mission_info_updated_success')
          );
          return res.data;
        }),
        catchError(err => {
          this.toastService.showErrorToastsResponse(err);
          this.toastService.showErrorToasts(
            this.translateService.instant('modules.dashboard.service.mission_info_updated_error')
          );
          return throwError(err);
        })
      ).toPromise();
  }

  public updateMissionsInfoForOrder(orderId: string, data: any): Promise<Mission[]> {
    return this.http.put(`${environment.backendUrl}admin/missions/missions-order-info/${orderId}`,
      data)
      .pipe(
        map((res: any) => {
          this.toastService.showSuccessToast(
            this.translateService.instant('modules.dashboard.service.mission_info_updated_success')
          );
          return res.data;
        }),
        catchError(err => {
          this.toastService.showErrorToastsResponse(err);
          this.toastService.showErrorToasts(
            this.translateService.instant('modules.dashboard.service.mission_info_updated_error')
          );
          return throwError(err);
        })
      ).toPromise();
  }


  public updateMissionComments(missionId: string, data: any): Promise<Mission> {
    return this.http.put(`${environment.backendUrl}admin/missions/note/${missionId}`,
      data)
      .pipe(
        map((res: any) => {
          this.toastService.showSuccessToast(
            this.translateService.instant('modules.dashboard.service.mission_comments_updated_success')
          );
          return res.data;
        }),
        catchError(err => {
          this.toastService.showErrorToastsResponse(err);
          this.toastService.showErrorToasts(
            this.translateService.instant('modules.dashboard.service.mission_comments_updated_error')
          );
          return throwError(err);
        })
      ).toPromise();
  }

  public updateMissionFollowUp(missionId: string, data: any): Promise<Mission> {
    return this.http.put(`${environment.backendUrl}admin/missions/follow-up/${missionId}`,
      data)
      .pipe(
        map((res: any) => {
          this.toastService.showSuccessToast(
            this.translateService.instant('modules.dashboard.service.mission_follow_up_updated_success')
          );
          return res.data;
        }),
        catchError(err => {
          this.toastService.showErrorToastsResponse(err);
          this.toastService.showErrorToasts(
            this.translateService.instant('modules.dashboard.service.mission_follow_up_updated_error')
          );
          return throwError(err);
        })
      ).toPromise();
  }

  public getMissionHistory(missionId: string, filteringObj: BackendFilteringObject): Promise<BackendPaginationResponse<any>> {
    return this.http.get(`${environment.backendUrl}missions/log/${missionId}?paginate=true&${filteringObj.getBackendFiltering()}`)
      .pipe(
        map((res: any) => res),
        catchError(err => {
          this.toastService.showErrorToasts(
            this.translateService.instant('modules.dashboard.service.get_mission_history_logs_error')
          );
          return throwError(err);
        })
      ).toPromise();
  }

  public getMissionPaymentHistory(orderId: string, filteringObj: BackendFilteringObject): Promise<BackendPaginationResponse<any>> {
    return this.http.get(`${environment.backendUrl}missions/paymentLog/${orderId}?paginate=true&${filteringObj.getBackendFiltering()}`)
      .pipe(
        map((res: any) => res),
        catchError(err => {
          this.toastService.showErrorToasts(
            this.translateService.instant('modules.dashboard.service.get_mission_payment_logs_error')
          );
          return throwError(err);
        })
      ).toPromise();
  }

  public getEntityUsers(entityId: string): Promise<CustomerEntity> {
    return this.http.get(`${environment.backendUrl}admin/entities/${entityId}`)
      .pipe(
        map((res: any) => res.data),
        catchError(err => {
          this.toastService.showErrorToastsResponse(err);
          this.toastService.showErrorToasts(
            this.translateService.instant('modules.dashboard.service.get_entities_error')
          );
          return throwError(err);
        })
      ).toPromise();
  }

  private async getMissionDocumentFormData(document: CommonMissionDocumentUpload, missionId?: string): Promise<FormData> {
    if (!document.missionFile && document.url && document.filename) {
      document.missionFile = await this.downloadMissionFile(document.url, document.filename);
      document.users = document.users.map(u => u.id);
    }
    const params = new FormData();
    params.append('missionFile', document.missionFile);
    params.append('name', document.name);
    params.append('users', document.users.join(','));
    params.append('sendToValet', document.sendToValet ? '1' : '0');
    params.append('requireEndUserApproval', document.requireEndUserApproval ? '1' : '0');
    params.append('valetDocumentUploadRequired', document.valetDocumentUploadRequired ? 'true' : 'false');
    params.append('endUserContact', document.endUserContact);
    if (missionId) {
      params.append('missionId', missionId);
    }
    if (document.documentType) {
      params.append('documentType', document.documentType as string);
    }
    return params;
  }

  private async downloadMissionFile(url: string, filename: string) {
    return fetch(url)
      .then(e => e.blob())
      .then((blob) => {
        const b: any = blob;
        b.lastModifiedDate = new Date();
        b.name = filename;
        return new File([b], filename);
      });
  }

  public async uploadMissionDocument(document: CommonMissionDocumentUpload, missionId: string): Promise<any> {
    const params = await this.getMissionDocumentFormData(document, missionId);
    return this.http.post(`${environment.backendUrl}admin/missions/file`,
      params)
      .pipe(
        map((res: any) => {
          this.toastService.showSuccessToast(
            this.translateService.instant('modules.dashboard.service.upload_mission_document_success')
          );
          return res;
        }),
        catchError(err => {
          this.toastService.showErrorToastsResponse(err);
          this.toastService.showErrorToasts(
            this.translateService.instant('modules.dashboard.service.upload_mission_document_error')
          );
          return throwError(err);
        })
      ).toPromise();
  }

  public async updateMissionDocument(document: CommonMissionDocumentUpload, documentId: string): Promise<any> {
    const params = await this.getMissionDocumentFormData(document);
    return this.http.put(`${environment.backendUrl}admin/missions/file/${documentId}`,
      params)
      .pipe(
        map((res: any) => {
          this.toastService.showSuccessToast(
            this.translateService.instant('modules.dashboard.service.update_mission_document_success')
          );
          return res;
        }),
        catchError(err => {
          this.toastService.showErrorToastsResponse(err);
          this.toastService.showErrorToasts(
            this.translateService.instant('modules.dashboard.service.update_mission_document_error')
          );
          return throwError(err);
        })
      ).toPromise();
  }

  public deleteMissionDocument(documentId: string): Promise<any> {
    return this.http.delete(`${environment.backendUrl}admin/missions/file/${documentId}`)
      .pipe(
        map((res: any) => {
          this.toastService.showSuccessToast(
            this.translateService.instant('modules.dashboard.service.delete_mission_document_success')
          );
          return res;
        }),
        catchError(err => {
          this.toastService.showErrorToastsResponse(err);
          this.toastService.showErrorToasts(
            this.translateService.instant('modules.dashboard.service.delete_mission_document_error')
          );
          return throwError(err);
        })
      ).toPromise();
  }

  public getMissionDocuments(missionId: string, filteringObj: BackendFilteringObject): Promise<BackendPaginationResponse<any>> {
    return this.http.get(`${environment.backendUrl}admin/missions/files/${missionId}?paginate=true&${filteringObj.getBackendFiltering()}`)
      .pipe(
        map((res: any) => res),
        catchError(err => {
          this.toastService.showErrorToastsResponse(err);
          this.toastService.showErrorToasts(
            this.translateService.instant('modules.dashboard.service.get_mission_document_files_error')
          );
          return throwError(err);
        })
      ).toPromise();
  }

  public getMissionValetDocuments(missionId: string, filteringObj: BackendFilteringObject): Promise<BackendPaginationResponse<any>> {
    return this.http.get(`${environment.backendUrl}qualities/valid-documents/${missionId}?paginate=true&${filteringObj.getBackendFiltering()}`)
      .pipe(
        map((res: any) => res),
        catchError(err => {
          this.toastService.showErrorToastsResponse(err);
          this.toastService.showErrorToasts(
            this.translateService.instant('modules.dashboard.service.get_mission_document_files_error')
          );
          return throwError(err);
        })
      ).toPromise();
  }

  public updateMissionStatus(missionId: string, status: string, note?: string): Promise<any> {
    return this.http.put(`${environment.backendUrl}admin/missions/update-status/${missionId}`, {
      status,
      note
    })
      .pipe(
        map((res: any) => {
          this.toastService.showSuccessToast(
            this.translateService.instant('modules.dashboard.service.update_mission_status_success')
          );
          return res;
        }),
        catchError(err => {
          this.toastService.showErrorToastsResponse(err);
          this.toastService.showErrorToasts(
            this.translateService.instant('modules.dashboard.service.update_mission_status_error')
          );
          return throwError(err);
        })
      ).toPromise();
  }

  public confirmMissions(missionId: string | string[]): Promise<any> {
    const id = Array.isArray(missionId) ? missionId.join(',') : missionId;
    return this.http.put(`${environment.backendUrl}admin/missions/confirm/${id}`, {})
      .pipe(
        map((res: any) => {
          this.toastService.showSuccessToast(
            this.translateService.instant('modules.dashboard.service.update_mission_status_success')
          );
          return res;
        }),
        catchError(err => {
          this.toastService.showErrorToastsResponse(err);
          this.toastService.showErrorToasts(
            this.translateService.instant('modules.dashboard.service.update_mission_status_error')
          );
          return throwError(err);
        })
      ).toPromise();
  }

  public rescheduleMission(missionId: string): Promise<any> {
    return this.http.put(`${environment.backendUrl}admin/missions/reschedule/${missionId}`, {})
      .pipe(
        map((res: any) => {
          this.toastService.showSuccessToast(
            this.translateService.instant('modules.dashboard.service.update_mission_status_success')
          );
          return res;
        }),
        catchError(err => {
          this.toastService.showErrorToastsResponse(err);
          this.toastService.showErrorToasts(
            this.translateService.instant('modules.dashboard.service.update_mission_status_error')
          );
          return throwError(err);
        })
      ).toPromise();
  }

  public assignSubcontractor(missionId: string, subcontractorId: string): Promise<any> {
    return this.http.put(`${environment.backendUrl}admin/missions/assign-subcontractor/${missionId}`, {
      subcontractorId
    })
      .pipe(
        map((res: any) => {
          this.toastService.showSuccessToast(
            this.translateService.instant('modules.dashboard.service.assign_subcontractor_success')
          );
          return res;
        }),
        catchError(err => {
          this.toastService.showErrorToastsResponse(err);
          this.toastService.showErrorToasts(
            this.translateService.instant('modules.dashboard.service.assign_subcontractor_error')
          );
          return throwError(err);
        })
      ).toPromise();
  }

  public cancelMission(missionId: string | string[], data?: any): Promise<any> {
    const id = Array.isArray(missionId) ? missionId.join(',') : missionId;
    return this.http.put(`${environment.backendUrl}admin/missions/cancel/${id}`, {
      note: data.note,
      anomalyType: data.anomalyType,
      costPercentage: data.costPercentage,
      pricePercentage: data.pricePercentage,
      secondaryPrice: data.secondaryPrice,
      costOther: data.costOther
    })
      .pipe(
        map((res: any) => {
          this.toastService.showSuccessToast(
            this.translateService.instant('modules.dashboard.service.cancel_mission_success')
          );
          return res;
        }),
        catchError(err => {
          this.toastService.showErrorToastsResponse(err);
          this.toastService.showErrorToasts(
            this.translateService.instant('modules.dashboard.service.cancel_mission_error')
          );
          return throwError(err);
        })
      ).toPromise();
  }

  public getCancelPercentage(missionId: string): Promise<any> {
    return this.http.get(`${environment.backendUrl}admin/missions/get-cancel-percentage/${missionId}`)
      .pipe(
        map((res: any) => {
          return res.data;
        }),
        catchError(err => {
          this.toastService.showErrorToastsResponse(err);
          this.toastService.showErrorToasts(
            this.translateService.instant('modules.dashboard.service.get_cancel_percentage_error')
          );
          return throwError(err);
        })
      ).toPromise();
  }

  public declineMission(missionId: string | string[], data: any): Promise<any> {
    const id = Array.isArray(missionId) ? missionId.join(',') : missionId;

    return this.http.put(`${environment.backendUrl}admin/missions/decline/${id}`, data)
      .pipe(
        map((res: any) => {
          this.toastService.showSuccessToast(
            this.translateService.instant('modules.dashboard.service.decline_mission_success')
          );
          return res;
        }),
        catchError(err => {
          this.toastService.showErrorToastsResponse(err);
          this.toastService.showErrorToasts(
            this.translateService.instant('modules.dashboard.service.decline_mission_error')
          );
          return throwError(err);
        })
      ).toPromise();
  }

  public setSncfBonus(missionId: string, sncfBonus: number): Promise<any> {
    return this.http.put(`${environment.backendUrl}admin/missions/set-sncf-bonus/${missionId}`, {
      sncfBonus
    })
      .pipe(
        map((res: any) => {
          this.toastService.showSuccessToast(
            this.translateService.instant('modules.dashboard.service.set_sncf_bonus_success')
          );
          return res;
        }),
        catchError(err => {
          this.toastService.showErrorToastsResponse(err);
          this.toastService.showErrorToasts(
            this.translateService.instant('modules.dashboard.service.set_sncf_bonus_error')
          );
          return throwError(err);
        })
      ).toPromise();
  }

  public deleteMission(missionId: string): Promise<any> {
    return this.http.delete(`${environment.backendUrl}admin/missions/${missionId}`)
      .pipe(
        map((res: any) => {
          this.toastService.showSuccessToast(
            this.translateService.instant('modules.dashboard.service.delete_mission_success')
          );
          return res;
        }),
        catchError(err => {
          this.toastService.showErrorToastsResponse(err);
          this.toastService.showErrorToasts(
            this.translateService.instant('modules.dashboard.service.delete_mission_error')
          );
          return throwError(err);
        })
      ).toPromise();
  }

  public getMissionAnomaliesTypesGroups(): AnomalyTypesGroup[] {
    return [
      ...this.getCustomerMissionAnomaliesTypes(),
      ...this.getValetMissionAnomaliesTypes(),
      ...this.getPopValetMissionAnomaliesTypes()
    ];
  }

  public updateAnomaliesSource(source: string) {
    let anomalyTypesGroups = [];
    if (source === 'customer') {
      anomalyTypesGroups = this.getCustomerMissionAnomaliesTypes();
    } else if (source === 'valet') {
      anomalyTypesGroups = this.getValetMissionAnomaliesTypes();
    } else if (source === 'popvalet') {
      anomalyTypesGroups = this.getPopValetMissionAnomaliesTypes();
    }
    anomalyTypesGroups.map(group => {
      group.types = group.types.filter(t => !t.disabled);
    });
    return anomalyTypesGroups;
  }

  public getDelayRanges(): { value: number; name: string }[]  {
    return [
      { value: 30, name: 'modules.mission.service.anomaly.type.valet.delays.30' },
      { value: 60, name: 'modules.mission.service.anomaly.type.valet.delays.60' },
      { value: 90, name: 'modules.mission.service.anomaly.type.valet.delays.90' },
      { value: 120, name: 'modules.mission.service.anomaly.type.valet.delays.120' },
      { value: 150, name: 'modules.mission.service.anomaly.type.valet.delays.150' },
      { value: 180, name: 'modules.mission.service.anomaly.type.valet.delays.180' },
      { value: 210, name: 'modules.mission.service.anomaly.type.valet.delays.210' },
    ];
  }

  public getCustomerMissionAnomaliesTypes(): AnomalyTypesGroup[] {
    const types = [
      { value: CustomerAnomalySubtype.CONFLICTING_COMMUNICATION },
      { value: CustomerAnomalySubtype.VEHICLE_WAITING, popvaletCancel: true },
      { value: CustomerAnomalySubtype.VEHICLE_NOT_READY, popvaletCancel: true },
      { value: CustomerAnomalySubtype.VEHICLE_NOT_AVAILABLE, popvaletCancel: true },
      { value: CustomerAnomalySubtype.LATE_CANCELLATION, disabled: true },
      { value: CustomerAnomalySubtype.VEHICLE_FAILURE, popvaletCancel: true },
      { value: CustomerAnomalySubtype.RESCHEDULING, popvaletCancel: true },
      { value: CustomerAnomalySubtype.REQUEST, popvaletCancel: true }
    ];
    return [this.buildAnomalyTypesGroup('modules.mission.service.anomaly.type.customer.name', types)];
  }

  public getValetMissionAnomaliesTypes(): AnomalyTypesGroup[] {
    return [
      this.buildAnomalyTypesGroup('modules.mission.service.anomaly.type.valet.group_classic_title', [
        { value: ValetAnomalySubtype.SERVICE_OMISSION },
        { value: ValetAnomalySubtype.SERVICE_NO_REFILL },
        { value: ValetAnomalySubtype.MISSING_DOCUMENT, disabled: true },
        { value: ValetAnomalySubtype.INVALID_DOCUMENT, disabled: true },
        { value: ValetAnomalySubtype.BEHAVIOUR, popvaletCancel: true },
        { value: ValetAnomalySubtype.OUTFIT, popvaletCancel: true },
        { value: ValetAnomalySubtype.HIJACKING, popvaletCancel: true },
        { value: ValetAnomalySubtype.AT_FAULT_ACCIDENT },
        { value: ValetAnomalySubtype.NOT_AT_FAULT_ACCIDENT },
        { value: ValetAnomalySubtype.VEHICLE_FAILURE, popvaletCancel: true },
        { value: ValetAnomalySubtype.WITHDRAWAL, popvaletCancel: true },
        { value: ValetAnomalySubtype.ABANDON, popvaletCancel: true },
        { value: ValetAnomalySubtype.MISSION_DEFERRAL }
      ]),
      this.buildAnomalyTypesGroup('modules.mission.service.anomaly.type.valet.traffic_ticket_title', [
        { value: ValetAnomalySubtype.TRAFFIC_TICKET_LT_20 },
        { value: ValetAnomalySubtype.TRAFFIC_TICKET_20_TO_40 },
        { value: ValetAnomalySubtype.TRAFFIC_TICKET_GT_40 },
        { value: ValetAnomalySubtype.TRAFFIC_TICKET_UNPAID_TOLL },
        { value: ValetAnomalySubtype.TRAFFIC_TICKET_PARKING },
        { value: ValetAnomalySubtype.TRAFFIC_TICKET_OLD, disabled: true },
      ]),
      this.buildAnomalyTypesGroup('modules.mission.service.anomaly.type.valet.delay_title', [
        { value: ValetAnomalySubtype.DELAY },
        { value: ValetAnomalySubtype.IN_ADVANCE }
      ]),
      this.buildAnomalyTypesGroup('modules.mission.service.anomaly.type.valet.excess_mileage_title', [
        { value: ValetAnomalySubtype.MILEAGE }
      ]),
      this.buildAnomalyTypesGroup('modules.mission.service.anomaly.type.valet.customer_request_title', [
        { value: ValetAnomalySubtype.CUSTOMER_FINANCIAL },
        { value: ValetAnomalySubtype.CUSTOMER_TRANSPORT }
      ])
    ];
  }

  public getPopValetMissionAnomaliesTypes(): AnomalyTypesGroup[] {
    const types = [
      { value: PopValetAnomalySubtype.LATE_CANCELLATION },
      { value: PopValetAnomalySubtype.SCHEDULING_ERROR, popvaletCancel: true }
    ];
    return [this.buildAnomalyTypesGroup('modules.mission.service.anomaly.type.popvalet.name', types)];
  }

  public filterCancelAnomalyTypes(groups: AnomalyTypesGroup[]): AnomalyTypesGroup[] {
    return groups.map(group => ({
      ...group,
      types: group.types.filter(t => t.popvaletCancel)
    })).filter(group => group.types.length > 0);
  }

  private buildAnomalyTypesGroup(
    groupKey: string,
    types: { value: string, popvaletCancel?: boolean, disabled?: boolean }[]
  ): AnomalyTypesGroup {
    return {
      name: this.translateService.instant(groupKey),
      types: types.map(t => ({
        name: this.translateService.instant(`modules.mission.service.anomaly.type.${t.value}`),
        ...t
      }))
    };
  }

  public getMissionAnomalies(missionId: string, filteringObj: BackendFilteringObject): Promise<BackendPaginationResponse<any>> {
    return this.http.get(`${environment.backendUrl}admin/missions/anomaly/${missionId}?paginate=true&${filteringObj.getBackendFiltering()}`)
      .pipe(
        map((res: any) => res),
        catchError(err => {
          this.toastService.showErrorToasts(
            this.translateService.instant('modules.mission.service.get_mission_anomalies_error')
          );
          return throwError(err);
        })
      ).toPromise();
  }

  public createMissionAnomaly(missionAnomaly: CommonMissionAnomaly, missionId: string): Promise<any> {
    console.log(missionAnomaly);
    const formData = new FormData();
    formData.append('missionId', missionId);
    formData.append('type', missionAnomaly.type);
    formData.append('comment', missionAnomaly.comment);
    if (missionAnomaly.valet) {
      formData.append('valet', missionAnomaly.valet);
    }
    if (missionAnomaly.documents) {
      for (const document of missionAnomaly.documents) {
        formData.append('documents', document, document.name);
      }
    }
    return this.http.post(`${environment.backendUrl}admin/missions/anomaly`, formData)
      .pipe(
        map((res: any) => {
          this.toastService.showSuccessToast(
            this.translateService.instant('modules.mission.service.new_anomaly_success')
          );
          return res;
        }),
        catchError(err => {
          this.toastService.showErrorToastsResponse(err);
          this.toastService.showErrorToasts(
            this.translateService.instant('modules.mission.service.new_anomaly_error')
          );
          return throwError(err);
        })
      ).toPromise();
  }

  public postPositiveFeedback(missionId: string, comment?: string): Promise<any> {
    return this.http.post(`${environment.backendUrl}missions/customer-feedback`, {
      missionId,
      comment
    })
      .pipe(
        map((res: any) => {
          this.toastService.showSuccessToast(
            this.translateService.instant('modules.mission.service.send_positive_feedback')
          );
          return res.data;
        }),
        catchError(err => {
          this.toastService.showErrorToastsResponse(err);
          return throwError(err);
        })
      ).toPromise();
  }

  public deleteMissionAnomaly(missionAnomalyId: string): Promise<any> {
    return this.http.delete(`${environment.backendUrl}admin/missions/anomaly/${missionAnomalyId}`)
      .pipe(
        map((res: any) => {
          this.toastService.showSuccessToast(
            this.translateService.instant('modules.mission.service.delete_anomaly_success')
          );
          return res;
        }),
        catchError(err => {
          this.toastService.showErrorToastsResponse(err);
          this.toastService.showErrorToasts(
            this.translateService.instant('modules.mission.service.delete_anomaly_error')
          );
          return throwError(err);
        })
      ).toPromise();
  }

  public downloadOldMissionFile(missionFileId: string): Promise<any> {
    return this.http.get(`${environment.backendUrl}missions/files/download/${missionFileId}`)
      .pipe(
        map((res: any) => res.data),
        catchError(err => {
          this.toastService.showErrorToasts(
            this.translateService.instant('modules.mission.service.get_mission_file_error')
          );
          return throwError(err);
        })
      ).toPromise();
  }

  public getRecommendedChargingCount(brand: string, model: string, distance: number) {
    return this.http.post(`${environment.backendUrl}mission-services/get-recommended-charging-count`, {
      brand,
      model,
      distance
    })
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError(err => {
          this.toastService.showErrorToastsResponse(err);
          return throwError(err);
        })
      ).toPromise();
  }

  public showDateTimeEditDialog(after: moment.Moment, before: moment.Moment, canEditDate: boolean, addressData?: any): Observable<{
    dialog: NbDialogRef<CommonMissionDatetimeDialogComponent>,
    data: any
  }> {
    this.datetimeDialog = this.dialogService.open(CommonMissionDatetimeDialogComponent, {
      context: {
        after,
        before,
        canEditDate,
        addressData
      },
    });
    return this.datetimeDialog.componentRef.instance.submitEvent.pipe(
      map(res => {
        return {
          dialog: this.datetimeDialog,
          data: res
        };
      })
    );
  }

  public showReparationsFormDialog(entity, type: string): Observable<{
    dialog: NbDialogRef<CommonMissionReparationsDialogComponent>,
    data: any
  }> {
    this.reparationsDialog = this.dialogService.open(CommonMissionReparationsDialogComponent, {
      context: {
        entity,
        type
      },
    });
    return this.reparationsDialog.componentRef.instance.submitEvent.pipe(
      map(res => {
        return {
          dialog: this.reparationsDialog,
          data: res
        };
      })
    );
  }

  public getPlateNumber(plateNumber: string) {
    return this.http.get(`${environment.backendUrl}plate-number/${plateNumber}`, {})
      .pipe(
        map((res: any) => res.data),
        catchError(err => {
          console.error(err);
          this.toastService.showErrorToastsResponse(err);
          return throwError(err);
        })
      ).toPromise();
  }

  public showAddressEditDialog(type: string, entityId: string, placeId: string): Observable<{
    dialog: NbDialogRef<CommonMissionAddressDialogComponent>,
    data: any
  }> {
    this.addressDialog = this.dialogService.open(CommonMissionAddressDialogComponent, {
      context: {
        type,
        entityId,
        placeId
      },
    });
    return this.addressDialog.componentRef.instance.submitEvent.pipe(
      map(res => {
        return {
          dialog: this.addressDialog,
          data: res
        };
      })
    );
  }

  public showContactEditDialog(title: string, firstName:string, lastName: string): Observable<{
    dialog: NbDialogRef<CommonMissionContactDialogComponent>,
    data: any
  }> {
    this.contactDialog = this.dialogService.open(CommonMissionContactDialogComponent, {
      context: {
        title,
        firstName,
        lastName,
      },
    });
    return this.contactDialog.componentRef.instance.submitEvent.pipe(
      map(res => {
        return {
          dialog: this.contactDialog,
          data: res
        };
      })
    );
  }

  public getBatchMessage(missionId: string): Promise<any> {
    return this.http.get(`${environment.backendUrl}admin/get-batch-message/${missionId}`)
      .pipe(
        map((res: any) => {
          return res.data;
        }),
        catchError(err => {
          this.toastService.showErrorToastsResponse(err);
          this.toastService.showErrorToasts(
            this.translateService.instant('modules.dashboard.service.get_batch_message_error')
          );
          return throwError(err);
        })
      ).toPromise();
  }

  public createReparationMission(missionData: any): Promise<any> {
    const formData = new FormData();
    formData.append('type', missionData.type);
    formData.append('localisation', missionData.localisation);
    formData.append('date', missionData.date);
    formData.append('plateNumber', missionData.plateNumber);
    formData.append('brand', missionData.brand);
    formData.append('model', missionData.model);
    formData.append('description', missionData.description);
    for (const picture of missionData.pictures) {
      formData.append('pictures', picture, picture.name);
    }
    return this.http.post(`${environment.backendUrl}/missions/create-reparation-mission`, formData)
      .pipe(
        map((res: any) => {
          this.toastService.showSuccessToast(
            this.translateService.instant('modules.missions.service.create_reparation_mission_succes')
          )
          return res.data;
        }),
        catchError(err => {
          this.toastService.showErrorToastsResponse(err);
          this.toastService.showErrorToasts(
            this.translateService.instant('modules.missions.service.create_reparation_mission_error')
          );
          return throwError(err);
        })
      ).toPromise();
  }

  public getMissionDelayedTypes() {
    return [{
      name: 'Client',
      value: 'customer.vehicle_not_available',
    }, {
      name: 'Valet',
      value: 'valet.withdrawal'
    }, {
      name: 'Pop Valet',
      value: 'popvalet.scheduling_error',
    }, {
      name: 'Aucun responsable',
      value: 'noone_at_fault'
    }
  ];
  }

  public showMissionDelayedDialog(valetId: string | null, missionStatus: string): Observable<{
    dialog: NbDialogRef<CommonMissionDelayedDialogComponent>,
    data: any
  }> {
    this.delayedDialog = this.dialogService.open(CommonMissionDelayedDialogComponent, {
      context: {
        valetId,
        missionStatus
      },
      closeOnBackdropClick: false,
      closeOnEsc: false
    });
    return this.delayedDialog.componentRef.instance.submitEvent.pipe(
      map(res => {
        return {
          dialog: this.delayedDialog,
          data: res
        };
      })
    );
  }

  public getDocumentTypesByEntity(entityId: string): Promise<any> {
    return this.http.get(`${environment.backendUrl}admin/document-types/by-entity/${entityId}`)
      .pipe(
        map((res: any) => {
          this.availableDocumentTypes = res.data;
          return res.data;
        }),
        catchError(err => {
          this.toastService.showErrorToastsResponse(err);
          return throwError(err);
        })
      ).toPromise();
  }

  public getCMRTypeId(): string | null {
    return this.getDocumentTypeIdByName('Lettre de transport - CMR');
  }

  public getDocumentTypeIdByName(typeName: string): string | null {
    if (!typeName || !this.availableDocumentTypes?.length) {
      return null;
    }

    const docType = this.availableDocumentTypes.find(
      dt => dt.name.toLowerCase().trim() === typeName.toLowerCase().trim()
    );

    return docType?.id || null;
  }

  public getDocumentTypeName(documentTypeId: string | any): string {
    if (!documentTypeId) {
      return '';
    }

    let id = documentTypeId;
    if (typeof documentTypeId === 'object') {
      id = documentTypeId.id || documentTypeId.value;
    }

    if (!this.availableDocumentTypes || this.availableDocumentTypes.length === 0) {
      return '';
    }

    const docType = this.availableDocumentTypes.find(dt => dt.value === id || dt.id === id);
    return docType?.name || '';
  }

  public generateReport(missionId: string, type: string): Promise<any> {
    return this.http.post(`${environment.backendUrl}admin/missions/generate-report/${missionId}/${type}`, {})
      .pipe(
        map((res: any) => {
          return res.data;
        }),
        catchError(err => {
          this.toastService.showErrorToastsResponse(err);
          return throwError(err);
        })
      ).toPromise();
  }

  public getValetPath(missionId: string): Promise<any> {
    return this.http.get(`${environment.backendUrl}missions/valet-path/${missionId}`)
      .pipe(
        map((res: any) => {
          return res.data;
        }),
        catchError(err => {
          this.toastService.showErrorToastsResponse(err);
          return throwError(err);
        })
      ).toPromise();
  }

  public formatType(row: any, role: any): string {
    let value = this.translateService.instant('modules.mission.mission_log_grid.event_type.' + row.type);
    if (row.note && !(row.note.startsWith('#') && row.note.endsWith('#') && role !== USER_ROLE.ADMIN)) {
      value += ' (' + row.note + ')';
    }
    return value;
  }

  public formatUser(row: any): string {
    return row.userId ? row.userId.firstName + ' ' + row.userId.lastName : '';
  }

  public formatPhone(row: any): string {
    return row.userId ? row.userId.phone : '';
  }

  public formatEmail(row: any): string {
    return row.userId ? row.userId.email : '';
  }

  public formatCreationDate(row: any): string {
    return moment(row.creationDate).format('DD.MM.YYYY HH:mm:ss');
  }

  public formatMissionPaymentDate(row: any): string {
    return moment(row.date).format('DD.MM.YYYY HH:mm:ss');
  }

  public formatDocumentType(row: any): string {
    return row.documentType ? row.documentType.name : '';
  }

  public formatValetDocumentUploadRequired(row: any): string {
    return row.valetDocumentUploadRequired ? this.translateService.instant('global.yes') : this.translateService.instant('global.no');
  }

  public exportValetInvoicesPageData(localData: any[], role: any): Promise<any> {
    try {
      let formattedExport = this.exportValetInvoicesParser(localData, role);
      this.exportService.export(formattedExport);
      return Promise.resolve(formattedExport);
    } catch (error) {
      this.toastService.showErrorToasts(
        this.translateService.instant('modules.missions.service.export_mission_log_error')
      );
      return Promise.reject(error);
    }
  }

  private exportValetInvoicesParser(dataToExport: any[], role: string): any[] {
    const formattedExport = [];
    dataToExport.forEach(row => {
      formattedExport.push({
        'Type': this.formatType(row, role),
        'Nom d\'utilisateur': this.formatUser(row),
        'Téléphone': this.formatPhone(row),
        'Email': this.formatEmail(row),
        'Créé le': this.formatCreationDate(row)
      });
    });
    return formattedExport;
  }

  public formatAddedBy(row: any): string {
    if (row.creatorId) {
      return `${row.creatorId.firstName} ${row.creatorId.lastName}`;
    }
    return '';
  }

  public formatAnomalyType(row: any): string {
    return this.anomalyTypesGroups
      .flatMap(g => g.types)
      .find(t => t.value === row.type)?.name || '';
  }

  public formatAnomalyComment(row: any): string {
    return row.comment ? row.comment.replace(/\n/g, '<br>') : '';
  }

  public formatAnomalyDocuments(row: any): string {
    return row.documents ? row.documents.map(doc => `<a href="${doc.url}" target="_blank">${doc.filename}</a>`).join(' - ') : '';
  }

  public formatAnomalyDate(row: any): string {
    return moment(row.date).format('DD.MM.YYYY HH:mm:ss');
  }

  public formatAnomalyCreator(row: any): string {
    return row.creator ? (row.creator.firstName + ' ' + row.creator.lastName) : '';
  }

  public exportMissionAnomalyPageData(localData: any[]): Promise<any> {
    try {
      let formattedExport = this.exportMissionAnomalyParser(localData);
      this.exportService.export(formattedExport);
      return Promise.resolve(formattedExport);
    } catch (error) {
      this.toastService.showErrorToasts(
        this.translateService.instant('modules.missions.service.export_mission_anomalies_error')
      );
      return Promise.reject(error);
    }
  }

  private exportMissionAnomalyParser(dataToExport: any[]): any[] {
    const formattedExport = [];
    dataToExport.forEach(row => {
      formattedExport.push({
        'Type': this.formatAnomalyType(row),
        'Commentaire': this.formatAnomalyComment(row),
        'Documents': this.formatAnomalyDocuments(row),
        'Date': this.formatAnomalyDate(row),
        'Créateur': this.formatAnomalyCreator(row)
      });
    });
    return formattedExport;
  }

  public displaySurchargePrice(role: USER_ROLE, entity: CustomerEntity) {
    return !!([
      USER_ROLE.CUSTOMER_ADMIN,
      USER_ROLE.CUSTOMER_USER,
      USER_ROLE.CUSTOMER_READ_ONLY
    ].includes(role) && entity.displaySurchargePricePercentage);
  }
}
